import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Product } from '../entities/products.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';

defineMailTemplate(MailTemplateType.PRODUKT_LOESCHUNG, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Workshop gelöscht',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
  <p>Das Produkt <b>{{product-name}}</b> wurde aus der Institution <b>{{institution-name}}</b> entfernt. </p>
  
  <p> Somit ist Ihre Anmeldung auch gelöscht. Bei Fragen kontaktieren Sie bitte den Support. </p>
 
  <p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
  <p>Viele Gr&uuml;&szlig;e</p>
 `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.PRODUKT_LOESCHUNG),
  attachments: [],
  displayName: 'Veranstaltungseintragung durch Admin',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.PRODUKT_LOESCHUNG),
});

export async function notifyProductDeletion(
  productID: string,
  deletedProduct: NarrowedEntity<
    Product,
    {
      name: 1;
      _class: 1;
      slotConfig: { start: 1; end: 1 };
      appointments: { start: 1; end: 1; location: 1 };
    }
  >,
) {
  // get institution id
  const product = await getByID(Product, productID)({ _id: 1, name: 1, institutionID: 1 });
  if (!product) throw new Error('product not found');

  // Get institution with color definitions
  const institution = await getByID(
    Institution,
    product.institutionID,
  )({
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found');

  // gather all participations
  const participations = await Product.getParticipantsForProduct(product._id);
  const users = participations.map(p => p.user);

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  // build email
  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institution._id,
    MailTemplateType.PRODUKT_LOESCHUNG,
  )(InstitutionMailTemplate.allFields);

  console.log('Send delete mail', product.name, institution.name);
  for (const user of users) {
    const token = await generateLoginToken({
      userID: user._id,
      userClass: 'User',
      ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
    });

    if (user && user.whatsappNotification && user.whatsappPhone && mailTemplate.whatsappNotification) {
      try {
        const whatsapp_template = getWhatsappTemplate(MailTemplateType.PRODUKT_LOESCHUNG);
        const { getWhatsapp } = await import('../whatsapp/util.js');
        const whatsapp = getWhatsapp();
        const { MessageType, ComponentType, ComponentParamaterType } = await import(
          '@adornis/whatsapp/server/types.js'
        );
        const result = await whatsapp.sendMessage({
          to: user.whatsappPhone,
          type: MessageType.TEMPLATE,
          content: {
            template: {
              name: whatsapp_template.name,
              language: {
                code: 'de',
              },
              components: [
                {
                  type: ComponentType.BODY,
                  parameters: [
                    {
                      type: ComponentParamaterType.TEXT,
                      text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                    },
                    {
                      type: ComponentParamaterType.TEXT,
                      text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                    },
                    {
                      type: ComponentParamaterType.TEXT,
                      text: product.name || '-',
                    },
                  ],
                },
              ],
            },
          },
        });
        logger.info({ result }, 'whatsapp result');
      } catch (e) {
        logger.error({ err: e }, 'Error sending whatsapp message');
      }
    } else if (mailTemplate.emailNotification) {
      const mailContent = await mailTemplate.buildEmail({
        [MailTemplateVars.TITLE]: user.title ?? '',
        [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
        [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
        [MailTemplateVars.PRODUCT_NAME]: product.name,
        [MailTemplateVars.INSTITUTION_NAME]: institution.name,
        [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
        [MailTemplateVars.PRODUCT_CARD]: InstitutionMailTemplate.mapProductVar(deletedProduct),
      } as any);

      if (!mailContent) continue;
      await sendInstitutionEmail({
        email: user.email,
        subject: mailTemplate.subject,
        content: mailContent,
        institutionID: institution._id,
      });
    }
  }
}
